import { api, nowRangeQuery, type NowRange } from "@signet/web-kit";
import type { BandId, Pages, Platform } from "../shared/flow.ts";

/* What the Subs pages read from /api/m/subs, typed once. Mirrors server/store.ts. */

export const BASE = "/api/m/subs";

/** `id` is "new:NAME" for an active creator with no numbers yet: the server makes their row on the first save. */
export type Account = { id: string; creator: string; platform: Platform };

/** byHand: set by hand for that month (subs_avg_override), no page split. Missing on answers cached before 2026-11-02. */
export type MonthAverage = Pages & { total: number; days: number; byHand?: boolean };

export type FlowRow = Account & Pages & { total: number; days: number; band: BandId; previous: number | null; monthAvg: MonthAverage | null; spark: (number | null)[] };

export type Flow = {
  /** The newest day a report can be for: yesterday in Melbourne, reported this afternoon. */
  latest: string;
  from: string;
  to: string;
  noData: boolean;
  updatedAt: string | null;
  accounts: number;
  reported: number;
  missing: string[];
  /** Fansly pages with counts in but no growth yet (nothing the day before to subtract). */
  countsOnly: (Account & { freeTotal: number | null; vipTotal: number | null })[];
  totals: Pages & { total: number };
  previousTotal: number | null;
  rows: FlowRow[];
  /** The month the averages are of (YYYY-MM): the one before the window's first day. */
  avgMonth: string;
  daily: { day: string; total: number }[];
};

export type UrgentRow = Account & { weekAvg: number; weekDays: number; monthAvg: number; change: number; verdict: "bad" | "good" | null };
export type Urgent = { day: string; weekFrom: string; avgMonth: string; tracked: Account[]; rows: UrgentRow[]; choices: Account[] };

export type Entry = {
  day: string;
  /** The newest day a report can be for: yesterday in Melbourne, reported this afternoon. */
  latest: string;
  /** For Fansly, freeTotal and vipTotal are the counts as typed; free and vip the growth worked out from them. */
  accounts: (Account & Pages & { freeTotal: number | null; vipTotal: number | null; entered: boolean })[];
  updatedAt: string | null;
  updatedBy: string | null;
};

export type MonthRow = Account & Pages & { total: number; days: number; previous: number | null };

export type Months = {
  month: string;
  previousFrom: string;
  previousTo: string;
  through: string;
  rows: MonthRow[];
  totals: Pages & { total: number };
  previousTotal: number | null;
  history: ({ month: string } & Pages & { total: number })[];
};

export const subsKeys = {
  all: ["subs"] as const,
  day: (day: string) => ["subs", "flow", "day", day] as const,
  // "2" since the answer gained daily and monthAvg (2026-10-13). Now keeps the last answer it saw in
  // the browser (useSeenQuery) and draws it first, so an old saved answer without them broke the
  // Sub flow card with "Cannot read properties of undefined (reading 'map')". A new shape, a new number.
  range: (range: NowRange) => ["subs", "flow", "range", 2, nowRangeQuery(range)] as const,
  entry: (day: string) => ["subs", "entry", day] as const,
  months: (month: string) => ["subs", "months", month] as const,
  urgent: (day: string) => ["subs", "urgent", day] as const,
};

export const fetchDay = (day?: string) => api<Flow>(`${BASE}/flow${day ? `?${new URLSearchParams({ day })}` : ""}`);
export const fetchRange = (range: NowRange) => api<Flow>(`${BASE}/flow?${nowRangeQuery(range)}`);
export const fetchEntry = (day?: string) => api<Entry>(`${BASE}/entry${day ? `?${new URLSearchParams({ day })}` : ""}`);
export const saveEntry = (day: string, values: ({ account: string; freeTotal?: number | null; vipTotal?: number | null } & Pages)[]) =>
  api<{ saved: number; cleared: number; entry: Entry }>(`${BASE}/entry`, { method: "PUT", body: { day, values } });
export const fetchMonths = (month?: string) => api<Months>(`${BASE}/months${month ? `?${new URLSearchParams({ month })}` : ""}`);
export const fetchUrgent = (day: string) => api<Urgent>(`${BASE}/urgent?${new URLSearchParams({ day })}`);
export const saveAverage = (account: string, month: string, total: number | null) =>
  api<{ ok: true }>(`${BASE}/average`, { method: "PUT", body: { account, month, total } });
export const saveUrgent = (accounts: string[]) => api<Urgent>(`${BASE}/urgent`, { method: "PUT", body: { accounts } });
/** A creator's Fansly page onto the list; creators themselves are added in Creators. */
export const addAccount = (input: { creator: string; platform: Platform }) => api<{ account: Account }>(`${BASE}/accounts`, { body: input });
export const removeAccount = (id: string) => api(`${BASE}/accounts/${encodeURIComponent(id)}`, { method: "DELETE" });

/** "Mon 13 Oct" for a YYYY-MM-DD day, read as that calendar day wherever the browser is. */
export function dayLabel(day: string, withYear = false): string {
  return new Date(`${day}T12:00:00Z`).toLocaleDateString(undefined, { weekday: "short", day: "numeric", month: "short", ...(withYear ? { year: "numeric" } : {}), timeZone: "UTC" });
}

export function monthLabel(month: string): string {
  return new Date(`${month}-15T12:00:00Z`).toLocaleDateString(undefined, { month: "long", year: "numeric", timeZone: "UTC" });
}

export function shiftDay(day: string, offset: number): string {
  return new Date(Date.parse(`${day}T00:00:00Z`) + offset * 86_400_000).toISOString().slice(0, 10);
}

/** "1:08pm", in the reader's own clock. */
export function timeLabel(iso: string): string {
  return new Date(iso).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" }).replace(/\s?([AP]M)$/i, (_, m: string) => m.toLowerCase());
}

/** "Sep" for "2026-09". */
export const shortMonth = (month: string) => new Date(`${month}-15T12:00:00Z`).toLocaleDateString(undefined, { month: "short", timeZone: "UTC" });
